import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Group, Stack} from '@mantine/core';
import {FC, useCallback} from 'react';
import {DefaultValues, SubmitHandler} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {z} from 'zod';
import {CodesService} from '../api/generated/CodesService';
import {CodeClassTypes, CodeClassTypesSchema} from '../api/generated/enums.ts';
import {CodeGetDto} from '../api/generated/index.defs.ts';
import {BasicPage} from '../components/basic-page.tsx';
import {createForm} from '../forms/index.tsx';
import {AdditionalParams} from '../hooks/paged-data-table/types.ts';
import {
  getActiveFiltersCount,
  PagedDataTableConfig,
  renderEditButton,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table.tsx';
import {useProduce} from '../hooks/use-produce.ts';
import {ROUTES} from '../routes';
import {AdvancedPagedRequest} from '../types/index.ts';
import {mapNullToUndefined} from '../utils/zod.ts';

type FilterState = AdditionalParams<typeof CodesService.getAll>;
type ComponentState = {
  filter: FilterState;
  tableState?: Parameters<typeof CodesService.getAll>[0];
};

const RuntimeConfig = {
  renderActions: (
    <Button component={Link} to={ROUTES.ADMIN.CODES.CREATE.path}>
      <Group gap={'xs'}>
        <FontAwesomeIcon icon={faPlus} /> New Code
      </Group>
    </Button>
  ),
};

export const CodeListing = () => {
  const [state, setState] = useProduce<ComponentState>({
    filter: {
      codeClass: '',
    },
  });

  const additionalParams = state.filter;

  const onStateChange = useCallback(
    (tableState: AdvancedPagedRequest<CodeGetDto>) => {
      setState((draft) => {
        draft.tableState = tableState;
      });
    },
    [setState],
  );

  const clearFilterState = useCallback(() => {
    setState((draft) => {
      draft.filter.codeClass = '';
    });
  }, [setState]);

  const pagedDataTable = usePagedDataTable(CodesService.getAll, TableConfig, {
    ...RuntimeConfig,
    additionalParams,
    onStateChange,
    clearFilterState,
    renderFilter: (dismiss) => (
      <TableFilters
        defaultValues={state.filter as FilterSchema}
        onSubmit={(filters) => {
          setState((draft) => {
            draft.filter.codeClass = filters.codeClass;
          });

          dismiss();
        }}
      />
    ),
    filterBadge: getActiveFiltersCount(state.filter),
  });

  return <BasicPage title='Codes'>{pagedDataTable}</BasicPage>;
};

const TableConfig = PagedDataTableConfig(CodesService.getAll, {
  columns: [
    {
      accessor: 'copy',
      title: '',
      render: (item) =>
        renderEditButton({
          item: {...item, id: Number(item.id)},
          descriptor: 'code',
          route: ROUTES.ADMIN.CODES.DETAIL.path,
        }),
      width: 50,
    },
    {
      accessor: 'description',
      sortable: true,
    },
    {
      accessor: 'codeClass',
      title: 'Code Class',
      sortable: true,
    },
    {
      accessor: 'sortOrder',
      title: 'Sort Order',
      sortable: true,
    },
    {
      accessor: 'inactive',
      title: 'Status',
      render: (item) => (item.inactive ? 'Inactive' : 'Active'),
    },
  ],
  defaultSort: {
    column: 'codeClass',
    direction: 'ASC',
  },
  searchFieldNames: ['codeClass', 'description'],
});

const filterSchema = z.object({
  codeClass: mapNullToUndefined(CodeClassTypesSchema.optional()),
}) satisfies z.Schema<FilterState>;
type FilterSchema = z.TypeOf<typeof filterSchema>;

const Form = createForm<FilterSchema>();

type TableFiltersProps = {
  defaultValues?: DefaultValues<FilterSchema>;
  onSubmit: SubmitHandler<FilterSchema>;
};

const TableFilters: FC<TableFiltersProps> = ({defaultValues, onSubmit}) => {
  return (
    <Form
      onSubmit={onSubmit}
      schema={filterSchema}
      defaultValues={defaultValues}
    >
      <Stack>
        <Form.Select
          clearable
          searchable
          name='codeClass'
          label='Code Class'
          enumObject={CodeClassTypes}
          placeholder='Select Code Class'
        />
        <Form.Submit>Apply</Form.Submit>
      </Stack>
    </Form>
  );
};
